/**
 * The BlankCutEditor container
 */
import React from "react";
import { useDispatch, useSelector } from "react-redux";
import PropTypes from "prop-types";
import { Container } from "reactstrap";
import Page from "../../components/Page";
import BlankCutEditorForm from "../../components/BlankCutEditorForm";
import CMSBlock from "../../components/CMSBlock";
import { getFormValues } from "redux-form";
import EditorFormHelpers from "../../components/BlankCutEditorForm/utils/BlankCutEditorFormHelpers";
import { VINYLS, VINYLTYPES } from "../../utils/vinyls";
import { useSsrEffect } from "@issr/core";
import { loadBlankCutEditor } from "../../redux/xapis";
import cmsBlocksSelectors from "../../redux/cmsblocks/selectors";
import filter from "lodash/filter";

const _ = {
  filter: filter,
};

/**
 * BlankCutEditor container, the landing page for logged in users
 */
const BlankCutEditor = (props, context) => {
  const dispatch = useDispatch();
  const {
    loadedBlankCutEditor,
    loadingBlankCutEditor,
    language,
    blankCutEditorCMSBlocks,
    currentFormState,
  } = useSelector((state) => ({
    currentFormState: EditorFormHelpers.extractFormState(
      getFormValues("blankCutEditorForm")(state)
    ),
    textEditor: state.xapis.textEditor,
    loadingBlankCutEditor: state.xapis.loadingBlankCutEditor,
    loadedBlankCutEditor: state.xapis.loadedBlankCutEditor,
    blankCutEditorCMSBlocks: cmsBlocksSelectors.selectCMSBlocksForPage(
      state,
      state.xapis.blankCutEditorCMSBlocksSlugs
    ),
    language: state.i18nState.lang,
  }));

  const dataLayer = [
    {
      event: "view_item",
      value: 100,
      items: [
        { id: "folienzuschnitt", google_business_vertical: "retail" },
        { id: "folienzuschnitt-2", google_business_vertical: "retail" },
        { id: "folienzuschnitt-3", google_business_vertical: "retail" },
        { id: "3001", google_business_vertical: "retail" },
        { id: "3040", google_business_vertical: "retail" },
        { id: "moebelfolie", google_business_vertical: "retail" },
      ],
    },
    {
      ecommerce: {
        detail: {
          actionField: {
            list: "LandingPage",
          },
          products: [
            {
              name: "Klebefolie Wunschgröße",
              id: "folienzuschnitt",
              price: "9.98",
              category: "Klebefolien|Folienzuschnitt",
            },
            {
              name: "Zuschnitt Wunschfarbe",
              id: "folienzuschnitt-2",
              price: "9.98",
              category: "Klebefolien|Folienzuschnitt",
            },
            {
              name: "Folienzuschnitt (321 Farben)",
              id: "folienzuschnitt-3",
              price: "9.83",
              category: "Klebefolien|Folienzuschnitt",
            },
            {
              name: "Klebefolie Weiß (matt) - selbstklebende Folie",
              id: "3001",
              price: "8.90",
              category: "Klebefolien|Folienzuschnitt",
            },
            {
              name: "Klebefolie Schwarz (matt) - selbstklebende Folie",
              id: "3040",
              price: "8.90",
              category: "Klebefolien|Folienzuschnitt",
            },
          ],
        },
      },
    },
  ];
  /** frontload async calls */
  const frontload = async () => {
    if (!loadedBlankCutEditor && !loadingBlankCutEditor) {
      dispatch(loadBlankCutEditor());
    }
  };

  useSsrEffect(frontload, [
    loadedBlankCutEditor,
    loadingBlankCutEditor,
    loadBlankCutEditor,
    language,
  ]);

  const presetFromUrl = new URLSearchParams(props.location.search); // editorType = product

  let initialMaterialSerie = presetFromUrl.get("materialSerie") || 1;
  let initialColor = null;

  if (presetFromUrl.get("vinyl")) {
    // default case, nothing selected
    const initialColorArray = VINYLS.filter(
      (vinyl) => parseInt(vinyl.id) === parseInt(presetFromUrl.get("vinyl"))
    );
    if (initialColorArray.length === 1) {
      initialColor = initialColorArray[0];
      initialMaterialSerie =
        Object.keys(VINYLTYPES).filter(
          (vinylType) => initialColor.series === VINYLTYPES[vinylType].key
        )[0] || 1;
    }
  }
  // default case, nothing selected
  const selectedVinyls = VINYLS.filter(
    (vinyl) => vinyl.series === VINYLTYPES[initialMaterialSerie].key
  );
  initialColor = initialColor || selectedVinyls[0];

  const initialValues = {
    materialSerie: initialMaterialSerie,
    color: { value: initialColor, label: initialColor.title },
    width: presetFromUrl.get("width") || 500,
    height: presetFromUrl.get("height") || 220,
    amount: 1,
  };

  const blank_cut_editor_intro = _.filter(
    blankCutEditorCMSBlocks,
    function (cmsBlock) {
      return cmsBlock.slug === "blank_cut_editor_intro";
    }
  );
  const blank_cut_editor_block_1 = _.filter(
    blankCutEditorCMSBlocks,
    function (cmsBlock) {
      return cmsBlock.slug === "blank_cut_editor_block_1";
    }
  );
  const blank_cut_editor_block_2 = _.filter(
    blankCutEditorCMSBlocks,
    function (cmsBlock) {
      return cmsBlock.slug === "blank_cut_editor_block_2";
    }
  );
  const blank_cut_editor_block_3 = _.filter(
    blankCutEditorCMSBlocks,
    function (cmsBlock) {
      return cmsBlock.slug === "blank_cut_editor_block_3";
    }
  );
  const blank_cut_editor_faqs = _.filter(
    blankCutEditorCMSBlocks,
    function (cmsBlock) {
      return cmsBlock.slug === "blank_cut_editor_faqs";
    }
  );
  return (
    <Page
      id="blankcuteditor"
      title={context.t("Your individual blank cut vinyl")}
      lang={language}
      dataLayer={dataLayer}
      pageType="blankcuteditor"
      description={context.t(
        "Easily create your custom adhesive letters in multiple colors and many great fonts"
      )}
      image="https://cdn.myfolie.de/media/banner/800x500.png"
    >
      <BlankCutEditorForm
        currentFormState={currentFormState}
        initialValues={initialValues}
        language={language}
        previewContent={
          <Container>
            {blank_cut_editor_intro[0] && (
              <CMSBlock cmsBlock={blank_cut_editor_intro[0]} />
            )}
          </Container>
        }
      />
      {blank_cut_editor_block_1[0] && (
        <Container className="my-5">
          <CMSBlock cmsBlock={blank_cut_editor_block_1[0]} />
        </Container>
      )}
      {blank_cut_editor_block_2[0] && (
        <Container className="my-5">
          <CMSBlock cmsBlock={blank_cut_editor_block_2[0]} />
        </Container>
      )}
      {blank_cut_editor_block_3[0] && (
        <Container className="my-5">
          <CMSBlock cmsBlock={blank_cut_editor_block_3[0]} />
        </Container>
      )}
      {blank_cut_editor_faqs[0] && (
        <Container className="my-5">
          <CMSBlock cmsBlock={blank_cut_editor_faqs[0]} />
        </Container>
      )}
    </Page>
  );
};

/**  define proptype for the 'translation' function  */
BlankCutEditor.contextTypes = {
  t: PropTypes.func,
};

/** export the component as redux connected component */
export default BlankCutEditor;
